// apps/share-web/src/text-share/text-share-api.ts
//
// Загрузка нагрузки текстовой ссылки: `GET /api/text-shares/:id`.
//
// Маршрут ПУБЛИЧНЫЙ — ни логина, ни команды, ни cookie: ссылка и есть пропуск
// (`apps/share-worker/functions/api/text-shares/[id].ts`). Отсюда и отсутствие
// `credentials: 'include'`, которое стоит у каждого соседа (setlist.ts, me.ts):
// просить у читателя без аккаунта его cookie значит просить то, чего нет, и
// заодно отправлять чужую cookie на страницу, которая о владельце не знает
// ничего.
//
// Три «нет» РАЗЛИЧАЮТСЯ, и это не аккуратность ради полноты:
//   · 410 — ссылка была ПРАВИЛЬНАЯ, и её сессию завершили в десктопе;
//   · 404 — такой ссылки не было вовсе (или её нагрузка потеряна);
//   · error — сеть или сервер, то есть «попробуй ещё раз».
// Решение Сергея от 28.08 требует, чтобы первое и второе выглядели по-разному:
// музыкант в зале должен понять, что он не ошибся ссылкой. Склейка здесь стёрла
// бы разницу до того, как страница получит шанс её показать.

import type { TextSharePayload } from '@bandway/share-types';

export type TextShareResult =
  | {
      state: 'ok';
      payload: TextSharePayload;
      /**
       * Ревизия содержимого из заголовка `X-Content-Rev` (спека §4.2).
       * `undefined` — воркер её ещё не отдаёт; страница тогда ведёт себя как до
       * ревизий, а не считает ревизию нулевой.
       */
      contentRev?: number;
    }
  /** Сессию завершили — ссылка была настоящая. */
  | { state: 'ended' }
  /** Такой ссылки нет (или её нагрузка не найдена: читать всё равно нечего). */
  | { state: 'not-found' }
  /** Сеть, сервер или неразборный ответ. */
  | { state: 'error' };

/**
 * Минимальная проверка формы. Полной валидации контракта здесь НЕ делается —
 * поля песни разбирают чистые функции поверхности (`words-model.ts`), и они
 * терпимы к пропускам по контракту (`chart` необязателен, `sections` может быть
 * пустым). Но «есть ли вообще список песен» решается здесь: без него страница
 * нарисовала бы пустую поверхность вместо честной ошибки, а пустая поверхность
 * читателю ничего не говорит.
 */
function looksLikePayload(body: unknown): body is TextSharePayload {
  if (body == null || typeof body !== 'object') return false;
  return Array.isArray((body as { songs?: unknown }).songs);
}

export async function fetchTextShare(
  id: string,
  apiBase = '',
  fetchImpl: typeof fetch = fetch,
): Promise<TextShareResult> {
  try {
    const res = await fetchImpl(`${apiBase}/api/text-shares/${encodeURIComponent(id)}`);
    if (res.status === 410) return { state: 'ended' };
    if (res.status === 404) return { state: 'not-found' };
    if (!res.ok) return { state: 'error' };
    const body: unknown = await res.json();
    if (!looksLikePayload(body)) return { state: 'error' };
    // Ревизия приходит ЗАГОЛОВКОМ (спека §4.2): тело — байты из R2 один в один,
    // и вложить поле туда значило бы заставить клиента предсказывать собственную
    // следующую ревизию при записи. Заголовка нет (воркер до выката) —
    // `undefined`, и страница ведёт себя ровно как вчера.
    const raw = res.headers?.get?.('X-Content-Rev');
    const rev = raw === null || raw === undefined ? NaN : Number(raw);
    return {
      state: 'ok',
      payload: body,
      contentRev: Number.isFinite(rev) ? rev : undefined,
    };
  } catch {
    return { state: 'error' };
  }
}
